import { Layout } from './Layout';
import { ArrowLeft, ChevronRight, List } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';

export default function LegalPageLayout({ title, subtitle, lastUpdate, sections, children }) {
  const [activeSection, setActiveSection] = useState('');
  const [showMobileToc, setShowMobileToc] = useState(false);
  const sectionRefs = useRef({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: '-100px 0px -70% 0px', threshold: 0 }
    );

    sections.forEach((section) => {
      const element = document.getElementById(section.id);
      if (element) {
        observer.observe(element);
        sectionRefs.current[section.id] = element;
      }
    });

    return () => observer.disconnect();
  }, [sections]);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const yOffset = -100;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
      setShowMobileToc(false);
    }
  };

  return (
    <Layout>
      <div className="bg-gradient-to-b from-gray-50 to-white min-h-screen">
        {/* Header */}
        <div className="bg-emerald-600 text-white py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-4">
            <Link to="/" className="inline-flex items-center gap-2 text-emerald-100 hover:text-white mb-6 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Retour à l'accueil
            </Link>
            <h1 className="text-3xl md:text-4xl font-bold mb-3">{title}</h1>
            <p className="text-emerald-100 text-lg">{subtitle}</p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-8 md:py-12">
          <div className="flex gap-8">
            {/* Sidebar - Table of Contents (Desktop) */}
            <aside className="hidden lg:block w-72 flex-shrink-0">
              <div className="sticky top-24">
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <List className="w-5 h-5 text-emerald-500" />
                    Table des matières
                  </h3>
                  <nav className="space-y-1">
                    {sections.map((section) => (
                      <button
                        key={section.id}
                        onClick={() => scrollToSection(section.id)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all duration-200 flex items-center gap-2 ${
                          activeSection === section.id
                            ? 'bg-emerald-50 text-emerald-700 font-medium'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                      >
                        <ChevronRight className={`w-4 h-4 flex-shrink-0 transition-transform ${
                          activeSection === section.id ? 'text-emerald-500 rotate-90' : 'text-gray-400'
                        }`} />
                        <span className="line-clamp-2">{section.title}</span>
                      </button>
                    ))}
                  </nav>
                </div>

                {/* Quick Links */}
                <div className="mt-6 bg-gray-50 rounded-xl p-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Documents légaux</h4>
                  <div className="space-y-2 text-sm">
                    <Link to="/cgu" className="block text-emerald-600 hover:text-emerald-700">CGU</Link>
                    <Link to="/cgv" className="block text-emerald-600 hover:text-emerald-700">CGV</Link>
                    <Link to="/politique-de-confidentialite" className="block text-emerald-600 hover:text-emerald-700">Confidentialité</Link>
                    <Link to="/mentions-legales" className="block text-emerald-600 hover:text-emerald-700">Mentions légales</Link>
                  </div>
                </div>
              </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 min-w-0">
              {/* Mobile TOC Toggle */}
              <div className="lg:hidden mb-6">
                <button
                  onClick={() => setShowMobileToc(!showMobileToc)}
                  className="w-full bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center justify-between"
                >
                  <span className="font-medium text-gray-900 flex items-center gap-2">
                    <List className="w-5 h-5 text-emerald-500" />
                    Table des matières
                  </span>
                  <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${showMobileToc ? 'rotate-90' : ''}`} />
                </button>
                
                {showMobileToc && (
                  <div className="mt-2 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                    <nav className="space-y-1">
                      {sections.map((section) => (
                        <button
                          key={section.id}
                          onClick={() => scrollToSection(section.id)}
                          className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center gap-2 ${
                            activeSection === section.id
                              ? 'bg-emerald-50 text-emerald-700 font-medium'
                              : 'text-gray-600'
                          }`}
                        >
                          <ChevronRight className={`w-4 h-4 flex-shrink-0 ${
                            activeSection === section.id ? 'text-emerald-500' : 'text-gray-400'
                          }`} />
                          {section.title}
                        </button>
                      ))}
                    </nav>
                  </div>
                )}
              </div>

              {/* Content Card */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-6 md:p-10">
                  {/* Last Update Badge */}
                  <div className="inline-flex items-center gap-2 bg-gray-100 text-gray-600 text-sm px-3 py-1 rounded-full mb-8">
                    <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                    Dernière mise à jour : {lastUpdate}
                  </div>

                  {/* Content */}
                  <div className="prose prose-gray prose-emerald max-w-none
                    prose-headings:scroll-mt-24
                    prose-h2:text-xl prose-h2:font-bold prose-h2:text-gray-900 prose-h2:mt-10 prose-h2:mb-4 prose-h2:pb-2 prose-h2:border-b prose-h2:border-gray-200
                    prose-h3:text-lg prose-h3:font-semibold prose-h3:text-gray-800 prose-h3:mt-6 prose-h3:mb-3
                    prose-p:text-gray-600 prose-p:leading-relaxed
                    prose-li:text-gray-600
                    prose-ul:my-4
                    prose-strong:text-gray-900
                    prose-a:text-emerald-600 prose-a:no-underline hover:prose-a:underline
                  ">
                    {children}
                  </div>
                </div>

                {/* Footer */}
                <div className="bg-gray-50 px-6 md:px-10 py-6 border-t border-gray-200">
                  <p className="text-sm text-gray-500 text-center">
                    Pour toute question concernant ce document, contactez-nous à{' '}
                    <a href="mailto:m.duclos@petites-natures.fr" className="text-emerald-600 hover:underline">
                      m.duclos@petites-natures.fr
                    </a>
                  </p>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>
    </Layout>
  );
}
